import React, { useState, useEffect } from 'react';
import {
  Alert, Box, Chip, Grid, IconButton, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import { CheckCircleOutline, HourglassBottom, HourglassTop, Refresh as RefreshIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { Kpi, Legend, Panel, SERIES } from '../components/ui';
import { tokens } from '../theme';

const STATUS_LABELS = {
  MATURE: 'Mature',
  PRESQUE_MATURE: 'Presque mature',
  IMMATURE: 'Immature'
};

const STATUS_COLORS = {
  MATURE: 'success',
  PRESQUE_MATURE: 'warning',
  IMMATURE: 'default'
};

const PbaMaturation = () => {
  const [pbaData, setPbaData] = useState({
    MATURE: [],
    PRESQUE_MATURE: [],
    IMMATURE: []
  });
  const [stats, setStats] = useState({
    MATURE: { count: 0, total_quantity: 0 },
    PRESQUE_MATURE: { count: 0, total_quantity: 0 },
    IMMATURE: { count: 0, total_quantity: 0 }
  });
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMaturationData();
  }, []);

  const loadMaturationData = async () => {
    try {
      setLoading(true);
      
      // Charger les lots de production depuis l'API stock
      const response = await fetch('/api/stock/lots-maturation', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const lots = await response.json();
        
        // Grouper par statut
        const grouped = {
          MATURE: lots.filter(lot => lot.est_mature || lot.status === 'MATURE'),
          PRESQUE_MATURE: lots.filter(lot => !lot.est_mature && lot.jours_restants <= 2 && lot.jours_restants >= 0),
          IMMATURE: lots.filter(lot => !lot.est_mature && lot.jours_restants > 2)
        };
        
        setPbaData(grouped);
        
        // Calculer les stats
        const newStats = {};
        Object.keys(grouped).forEach(status => {
          newStats[status] = {
            count: grouped[status].length,
            total_quantity: grouped[status].reduce((sum, lot) => sum + lot.quantite_restante, 0)
          };
        });
        setStats(newStats);
      } else {
        throw new Error('Erreur API');
      }
    } catch (error) {
      setError('Erreur lors du chargement des données de maturation');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabData = [
    { key: 'MATURE', label: 'PBA Matures', data: pbaData.MATURE },
    { key: 'PRESQUE_MATURE', label: 'PBA Presque Matures', data: pbaData.PRESQUE_MATURE },
    { key: 'IMMATURE', label: 'PBA Immatures', data: pbaData.IMMATURE }
  ];

  const total = stats.MATURE.total_quantity + stats.PRESQUE_MATURE.total_quantity + stats.IMMATURE.total_quantity;
  const parts = [
    ['Matures', SERIES.livrable, stats.MATURE.total_quantity],
    ['Presque matures', SERIES.production, stats.PRESQUE_MATURE.total_quantity],
    ['Immatures', SERIES.maturation, stats.IMMATURE.total_quantity],
  ];
  const statut = (lot) => (lot.est_mature ? 'MATURE' : lot.jours_restants <= 2 ? 'PRESQUE_MATURE' : 'IMMATURE');
  const fr = (d) => new Date(d).toLocaleDateString('fr-FR');

  return (
    <Layout title="Maturation des PBA" plain
      actions={<Tooltip title="Actualiser"><IconButton aria-label="Actualiser" onClick={loadMaturationData} disabled={loading}><RefreshIcon /></IconButton></Tooltip>}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}><Kpi icon={CheckCircleOutline} tint="green" label="Matures" value={stats.MATURE.total_quantity} sub={`${stats.MATURE.count} lot(s) · prêts pour livraison`} /></Grid>
        <Grid item xs={12} sm={4}><Kpi icon={HourglassBottom} tint="navy" label="Presque matures" value={stats.PRESQUE_MATURE.total_quantity} sub={`${stats.PRESQUE_MATURE.count} lot(s) · 2 jours ou moins`} /></Grid>
        <Grid item xs={12} sm={4}><Kpi icon={HourglassTop} tint="amber" label="Immatures" value={stats.IMMATURE.total_quantity} sub={`${stats.IMMATURE.count} lot(s) · non livrables`} /></Grid>
      </Grid>

      <Panel title="Répartition du stock en maturation" sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', height: 14, borderRadius: 1, overflow: 'hidden', bgcolor: '#EEF1F6', gap: '2px', mb: 1.5 }}>
          {total > 0 && parts.map(([l, c, v]) => v > 0 && <Box key={l} title={`${l} : ${v}`} sx={{ width: `${(v / total) * 100}%`, bgcolor: c }} />)}
        </Box>
        <Legend items={parts} />
      </Panel>

      <Panel flush>
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} variant="scrollable" sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          {tabData.map((tab) => <Tab key={tab.key} label={`${STATUS_LABELS[tab.key]} (${tab.data.length})`} />)}
        </Tabs>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type PBA</TableCell><TableCell>Référence</TableCell><TableCell align="right">Quantité</TableCell>
                <TableCell>Production</TableCell><TableCell>Maturation</TableCell><TableCell align="right">Âge</TableCell><TableCell>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tabData[currentTab].data.map((lot, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{lot.pba_type}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{lot.reference}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>
                    {lot.quantite_restante}
                    {lot.quantity_delivered > 0 && <Typography component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary', fontFamily: 'inherit' }}>livré {lot.quantity_delivered}</Typography>}
                  </TableCell>
                  <TableCell>{fr(lot.date_production)}</TableCell>
                  <TableCell>{fr(lot.maturation_date)}</TableCell>
                  <TableCell align="right">{lot.jours_ecoules} j</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={STATUS_LABELS[statut(lot)]} color={STATUS_COLORS[statut(lot)]} /></TableCell>
                </TableRow>
              ))}
              {tabData[currentTab].data.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? 'Chargement…' : `Aucun lot ${STATUS_LABELS[tabData[currentTab].key].toLowerCase()}`}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
    </Layout>
  );
};

export default PbaMaturation;